import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_DOCUMENT_CATEGORIES, categoriesForKind,
} from '@platform/shared-types';
import type { DocumentCategoryRecord, DocumentKind } from '@platform/shared-types';
import { documentsApi } from '../../services/documentsApi';

/**
 * THE CATEGORY, IN WORDS AN OPERATOR READS.
 *
 * `kind` says what a document IS — a policy or an agreement. `folderId` is how
 * you file it. Neither answers the question every picker asks: which documents
 * belong HERE? That is the category.
 *
 * CATEGORIES ARE ROWS NOW, not a hardcoded list. They are managed at
 * /legal/categories and served by documents-service, so "add an NDA category"
 * is an afternoon's admin rather than a four-repo deploy. What is still
 * hardcoded is a handful of IDS — the financing wizard asks for
 * `device_finance` by name — which is why the built-ins can be relabelled and
 * retired but never deleted.
 *
 * Everything here degrades to the compiled-in built-ins when the API cannot be
 * reached, so a dropdown is never empty and a label never renders as a raw id
 * because a request failed.
 */

/**
 * The compiled-in fallback, by id.
 *
 * Read defensively. `DEFAULT_DOCUMENT_CATEGORIES` is a RUNTIME value from the
 * linked `@platform/shared-types`, and a Vite dep pre-bundle built before that
 * export existed hands back `undefined` — at module scope, which white-screens
 * the whole app instead of failing where it is used. Degrading to an empty map
 * costs a dropdown its fallback labels; crashing costs the page. The console
 * line names the actual fix: delete `node_modules/.vite` and restart.
 */
const SEED: readonly DocumentCategoryRecord[] = (() => {
  if (Array.isArray(DEFAULT_DOCUMENT_CATEGORIES)) return DEFAULT_DOCUMENT_CATEGORIES;
  console.error(
    '@platform/shared-types exported no DEFAULT_DOCUMENT_CATEGORIES — a stale Vite pre-bundle. RESTART the dev server: deleting node_modules/.vite under a running server does nothing, because Vite keeps the optimised bundle in memory and rewrites the directory.',
  );
  return [];
})();

const BUILT_IN = new Map(SEED.map((c) => [c.categoryId, c] as const));

/**
 * Module-level cache, shared by every screen in one page load.
 *
 * Categories change perhaps twice a year and are read by four screens. A cache
 * here means opening a document does not re-fetch a list the page beside it
 * already has, and `refreshCategories()` is what the management page calls
 * after an edit so the rest of the app sees it without a reload.
 */
let cache: DocumentCategoryRecord[] | null = null;
let inFlight: Promise<DocumentCategoryRecord[]> | null = null;
const listeners = new Set<(c: DocumentCategoryRecord[]) => void>();

const publish = (categories: DocumentCategoryRecord[]) => {
  cache = categories;
  listeners.forEach((l) => l(categories));
};

async function fetchAll(): Promise<DocumentCategoryRecord[]> {
  const res = await documentsApi.listCategories();
  const categories = res.categories?.length ? res.categories : [...SEED];
  publish(categories);
  return categories;
}

/** Every category, from cache when warm. Falls back to the built-ins. */
export async function loadCategories(force = false): Promise<DocumentCategoryRecord[]> {
  if (cache && !force) return cache;
  if (!inFlight || force) {
    inFlight = fetchAll()
      .catch((e) => {
        console.error('[documentCategories] falling back to built-ins:', e?.message || e);
        const fallback = [...SEED];
        publish(fallback);
        return fallback;
      })
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Re-read after an edit, so every open screen picks the change up. */
export const refreshCategories = () => loadCategories(true);

/**
 * React binding. `kind` narrows to what belongs on that list — an agreements
 * page never offers "Cookie policy" — and omitting it returns everything,
 * which is what the management page and a global filter want.
 */
export function useDocumentCategories(kind?: DocumentKind) {
  const [all, setAll] = useState<DocumentCategoryRecord[]>(cache ?? [...SEED]);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let alive = true;
    listeners.add(setAll);
    void loadCategories().then(() => { if (alive) setLoading(false); });
    return () => { alive = false; listeners.delete(setAll); };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    await refreshCategories();
    setLoading(false);
  }, []);

  // Retired categories are dropped from every picker, but a document already
  // filed under one keeps its label — see `categoryLabel`.
  const categories = kind ? categoriesForKind(all, kind) : all.filter((c) => c.status !== 'retired');

  return {
    /** Offerable categories for this kind, in sort order. */
    categories,
    /** Everything, retired included — the management page needs both. */
    all,
    loading,
    reload,
    options: categories.map((c) => ({ value: c.categoryId, label: c.label, subtitle: c.hint })),
  };
}

/**
 * What a document's category should READ as — never a raw id, never a blank
 * cell.
 *
 * Resolution order is deliberate: the live row first (an operator may have
 * relabelled it), then the compiled-in built-in (the cache may not be warm
 * yet), then a humanised form of the id itself (an operator-created category
 * this build has never seen). Only a genuinely absent category reads as
 * "Uncategorised".
 */
export const categoryLabel = (category?: string | null): string => {
  if (!category) return 'Uncategorised';
  const live = cache?.find((c) => c.categoryId === category);
  if (live) return live.label;
  const builtIn = BUILT_IN.get(category);
  if (builtIn) return builtIn.label;
  return category.replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase());
};

/** The one-line hint under a label in a dropdown, when there is one. */
export const categoryHint = (category?: string | null): string | undefined => {
  if (!category) return undefined;
  return (cache?.find((c) => c.categoryId === category) ?? BUILT_IN.get(category))?.hint;
};

/**
 * Dropdown options WITHOUT a React hook, for the few call sites that build
 * options outside a component. Serves the cache when warm and the built-ins
 * otherwise — it never returns an empty list, because an empty category
 * dropdown reads as "there are none" rather than "this has not loaded".
 */
export const categoryOptions = (kind?: DocumentKind) => {
  const source = cache ?? [...SEED];
  const list = kind ? categoriesForKind(source, kind) : source.filter((c) => c.status !== 'retired');
  return list.map((c) => ({ value: c.categoryId, label: c.label, subtitle: c.hint }));
};

/**
 * The built-in labels, kept as an export because screens outside this folder
 * import it (the financing wizard groups its picker with it). Backed by the
 * seed list rather than a second hand-written map.
 */
export const CATEGORY_LABELS: Record<string, { label: string; hint: string }> =
  Object.fromEntries(
    SEED.map((c) => [c.categoryId, { label: c.label, hint: c.hint ?? '' }]),
  );
